import React, { useState } from 'react';
import { Row, Col, Input, Space, notification, Tag, Switch, Table } from 'antd';
import AppModal from 'components/modal/modal';
import { FilterValue, TablePaginationConfig } from 'antd/lib/table/interface';
import { DEFAULT_CURRENT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/config';
import { isEmpty } from 'lodash';
import { useGetAdminCompanies, useUpdateCompanyAdminStatus, GET_ADMIN_COMPANIES } from 'hooks/companiesAdminHooks';
import { AdminCompany } from 'services/companiesAdmin';
import { queryClient } from 'hooks';
import { Link } from 'react-router-dom';
import { ReactComponent as SearchIcon } from 'resources/icons/Search 38.svg';
import BreadCrumb from 'components/breadcrumb/BreadCrumb';
import Footer from 'components/layouts/footer/Foot';

const CompaniesList: React.FC = () => {
  const [filters, setFilters] = useState({
    pageNo: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_PAGE_SIZE,
    orderBy: 'name',
    orderType: 1,
    search: undefined as string | undefined,
  });

  const { isLoading, data } = useGetAdminCompanies(filters);

  const updateStatus = useUpdateCompanyAdminStatus();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCompanyId, setPendingCompanyId] = useState<string>('');
  const [pendingCompanyName, setPendingCompanyName] = useState<string>('');
  const [pendingCompanyStatus, setPendingCompanyStatus] = useState<boolean>(false);

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, pageNo: page, limit: pageSize });
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: any,
  ) => {
    setFilters({
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      orderBy: sorter?.field ? sorter?.field : 'name',
      orderType: sorter?.order === 'ascend' ? 1 : -1,
    });
  };

  const handleSearch = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e?.currentTarget?.value;
    setFilters({
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      search: !isEmpty(value) ? value : undefined,
    });
  };

  const onToggleStatus = async (record: AdminCompany, next: boolean) => {
    setPendingCompanyId(record.id);
    setPendingCompanyName(record.name);
    setPendingCompanyStatus(next);
    setShowConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!pendingCompanyId) return;
    try {
      await updateStatus.mutateAsync({ id: pendingCompanyId, isActive: pendingCompanyStatus });
      notification.success({ message: `Company ${pendingCompanyStatus ? 'activated' : 'deactivated'}` });
      queryClient.refetchQueries([GET_ADMIN_COMPANIES]);
    } catch (e: any) {
      notification.error({ message: e?.message || 'Failed to update status' });
    } finally {
      setPendingCompanyId('');
      setPendingCompanyName('');
      setPendingCompanyStatus(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <BreadCrumb />
        </Col>
      </Row>
      <div className="table-container">
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={16} />
          <Col span={8} className="tableSearchBar">
            <Space align="end">
              <Input
                placeholder="Search companies..."
                size="large"
                className="searchBar"
                onChange={handleSearch}
                suffix={<SearchIcon />}
                allowClear
              />
            </Space>
          </Col>
        </Row>

        <Table<AdminCompany>
          loading={isLoading}
          rowKey={r => r.id}
          dataSource={data?.data || []}
          onChange={handleTableChange}
          pagination={{
            current: filters.pageNo,
            pageSize: filters.limit,
            total: data?.total || 0,
            onChange: handlePageChange,
            showSizeChanger: true,
          }}
          columns={[
            { title: 'Name', dataIndex: 'name', sorter: true },
            { title: 'Email', dataIndex: 'email' },
            { title: 'Phone', dataIndex: 'phoneNumber' },
            { title: 'USDOT', dataIndex: 'usdot' },
            { title: 'Address', dataIndex: 'address' },
            {
              title: 'Status',
              dataIndex: 'isActive',
              render: (_: any, record: AdminCompany) => (
                <Space>
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    checked={record.isActive}
                    onChange={checked => onToggleStatus(record, checked)}
                  />
                </Space>
              ),
            },
            {
              title: 'Actions',
              render: (_: any, record: AdminCompany) => (
                <Space>
                  <Link to={`/companies/${record.id}`}>View</Link>
                </Space>
              ),
            },
          ]}
        />
        {showConfirmModal && (
          <AppModal
            title={pendingCompanyStatus ? 'Activate Company' : 'Deactivate Company'}
            body={() => (
              <p className="modal-body-title">
                You want to {pendingCompanyStatus ? 'activate' : 'deactivate'} <strong>{pendingCompanyName}</strong>?
              </p>
            )}
            onCancel={() => setShowConfirmModal(false)}
            onSubmit={() => handleConfirmToggle()}
          />
        )}
      </div>
      <Row>
        <Col span={24}>
          <Footer />
        </Col>
      </Row>
    </>
  );
};

export default CompaniesList;
